import React, { useCallback, useMemo } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { getSecondaryBreakdownFromTableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { determineDefaultValueForPrimaryFilter } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getEntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { useTouchableTooltipCollection } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { TableGrid } from 'apps/sigma2-ui/src/explore-section/components/TableGrid';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { generateColorPalette } from 'apps/sigma2-ui/src/lib/color-palette-generator';
import { getBackgroundTextColor } from 'apps/sigma2-ui/src/lib/color-palette-generator';
import { TableTooltip } from 'apps/sigma2-ui/src/explore-section/components/TableTooltip';
import { useFocusArea } from 'apps/sigma2-ui/src/lib/hooks/useFocusArea';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { getDimensionByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { callGenerateShareLink } from 'apps/sigma2-ui/src/state/generate-share-link';
import { getGroupByFromTableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { isEntitledToBiasAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';
import { useFormatCellValue } from 'apps/sigma2-ui/src/explore-section/hooks/useFormatCellValue';
import { MetricRequiresGrouping } from 'apps/sigma2-ui/src/explore-section/components/TableTooltip';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { isBiasAnalysisEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { DefaultOverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { OverallDisplayMode } from 'libs/end-to-end/src/view-configurations';

/**
 * Computes the display content for a table cell based on column position and display settings.
 */
function computeCellContent({
  columnIndex,
  rowIndex,
  cell,
  compatibleData,
  formatCellValue,
  displayOverallCount,
  metricInfo,
}: {
  columnIndex: number;
  rowIndex: number;
  cell: string;
  compatibleData: CompatibleTableData;
  formatCellValue: (args: { rawValue: string; header: string }) => string;
  displayOverallCount: boolean;
  metricInfo: {
    focusArea: FocusArea;
    metric: MyMetricDefinition;
  } | null;
}): string {
  let content =
    columnIndex === 0
      ? cell
      : formatCellValue({
          rawValue: cell,
          header: compatibleData.columnHeaders[columnIndex - 1],
        });

  if (displayOverallCount && columnIndex > 0) {
    const numeratorSize =
      compatibleData.numeratorSizes?.[rowIndex]?.[columnIndex];
    if (metricInfo?.metric.isMetricBoolean && numeratorSize) {
      content = String(numeratorSize);
    } else {
      content = 'N/A';
    }
  }

  return content;
}

/**
 * Checks if either level is privileged, which would prevent link generation.
 */
export function hasPrivilegedLevel(
  groupByLevel: LevelDefinition | undefined,
  secondaryLevel: LevelDefinition | undefined,
): boolean {
  return !!(groupByLevel?.isPrivileged || secondaryLevel?.isPrivileged);
}

/**
 * Checks if both parameters are defined dimension levels.
 */
export function areBothDimensionLevels(
  groupByLevel: LevelDefinition | undefined,
  secondaryLevel: LevelDefinition | undefined,
): boolean {
  return !!(groupByLevel && secondaryLevel);
}

/**
 * Determines the dimension and treatment level to use for deep dive links.
 * Returns null if we cannot generate a valid link.
 */
export function determineDimensionAndTreatmentLevel({
  groupByDimensionLevel,
  selectedGroupBy,
  groupByLevel,
  selectedSecondaryBreakdownDimensionDefinition,
  secondaryBreakdownDimensionLevel,
  secondaryBreakdownLevel,
  defaultDimension,
  defaultTreatmentDimensionLevel,
}: {
  groupByDimensionLevel: LevelDefinition | undefined;
  selectedGroupBy: string | undefined;
  groupByLevel: string;
  selectedSecondaryBreakdownDimensionDefinition:
    | DimensionDefinition
    | undefined;
  secondaryBreakdownDimensionLevel: LevelDefinition | undefined;
  secondaryBreakdownLevel: string;
  defaultDimension: string;
  defaultTreatmentDimensionLevel: string;
}): { dimension: string; treatmentDimensionLevel: string | undefined } | null {
  // Do not let them generate a link with a privileged level.
  if (
    hasPrivilegedLevel(groupByDimensionLevel, secondaryBreakdownDimensionLevel)
  ) {
    return null;
  }

  // We do not want to generate a link if both are dimension levels.
  if (
    areBothDimensionLevels(
      groupByDimensionLevel,
      secondaryBreakdownDimensionLevel,
    )
  ) {
    return null;
  }

  if (groupByDimensionLevel && selectedGroupBy) {
    return {
      dimension: selectedGroupBy,
      treatmentDimensionLevel: groupByLevel,
    };
  }

  if (
    selectedSecondaryBreakdownDimensionDefinition &&
    secondaryBreakdownDimensionLevel
  ) {
    return {
      dimension:
        selectedSecondaryBreakdownDimensionDefinition.columnHeaderValue,
      treatmentDimensionLevel: secondaryBreakdownLevel,
    };
  }

  return {
    dimension: defaultDimension,
    treatmentDimensionLevel: defaultTreatmentDimensionLevel,
  };
}

/**
 * Validates prerequisites for generating a deep dive link.
 * Returns true if all prerequisites are met.
 */
function canGenerateDeepDiveLink({
  isEmbedMode,
  selectedGroupBy,
  compatibleData,
  myObservation,
  lastSuccessfulCustomVizRequest,
}: {
  isEmbedMode: boolean;
  selectedGroupBy: string | undefined;
  compatibleData: CompatibleTableData | null;
  myObservation: unknown;
  lastSuccessfulCustomVizRequest: { customVizId: string } | null;
}): boolean {
  if (isEmbedMode) return false;
  if (!selectedGroupBy) return false;
  if (!compatibleData || !myObservation) return false;
  if (!lastSuccessfulCustomVizRequest) return false;
  if (lastSuccessfulCustomVizRequest.customVizId !== 'tableView') return false;
  return true;
}

/**
 * Validates focus area and entitlements for deep dive link generation.
 * Returns the focus area if entitled, or undefined if not entitled or not available.
 */
function getValidatedFocusArea(
  metricFocusArea: { focusArea: FocusArea; isHidden?: boolean } | undefined,
  myObservation: Parameters<typeof isEntitledToBiasAnalysis>[0] extends {
    features: infer F;
  }
    ? { features: F }
    : never,
): { focusArea: FocusArea } | undefined {
  if (!metricFocusArea || metricFocusArea.isHidden) return undefined;
  const isEntitled = isEntitledToBiasAnalysis({
    features: myObservation.features,
    focusArea: metricFocusArea.focusArea,
  });
  return isEntitled ? { focusArea: metricFocusArea.focusArea } : undefined;
}

function getIsBiasAnalysisEnabled(myObservation: MyObservation): boolean {
  const isEnabled = isBiasAnalysisEnabledForRevision({
    revisionFlags: myObservation,
  });

  const isEntitled = isEntitledToBiasAnalysis({
    features: myObservation.features,
    focusArea: null,
  });

  return isEnabled && isEntitled;
}

/**
 * Gets the dimension level for a given observation and selection.
 */
function getDimensionLevel(
  hasDefinition: boolean,
  myObservation: Parameters<typeof getLevelByColumnValueFromObservation>[0],
  columnHeaderValue: string,
  level: string,
): LevelDefinition | undefined {
  if (!hasDefinition) return undefined;
  return getLevelByColumnValueFromObservation(
    myObservation,
    columnHeaderValue,
    level,
  );
}

/**
 * Gets the default employee group values for fallback dimension selection.
 * Returns undefined if the employee group is not a string array.
 */
function getDefaultEmployeeGroupValues({
  allDimensions,
  getAllLevelsForDimension,
  focusArea,
  myObservation,
}: {
  allDimensions: Parameters<
    typeof determineDefaultValueForPrimaryFilter
  >[0]['allDimensions'];
  getAllLevelsForDimension: Parameters<
    typeof determineDefaultValueForPrimaryFilter
  >[0]['getAllLevelsForDimension'];
  focusArea: FocusArea;
  myObservation: Parameters<
    typeof determineDefaultValueForPrimaryFilter
  >[0]['myObservation'];
}): { dimension: string; level: string } | undefined {
  const defaultEmployeeGroup = determineDefaultValueForPrimaryFilter({
    feature: 'dataIntelligenceDeepDive',
    primaryFilterKey: 'employeeGroup',
    allDimensions,
    getAllLevelsForDimension,
    focusArea,
    myObservation,
    entitledFilterFeatureDetails:
      getEntitledDetailsInContextOfFiltersFeatureName({
        feature: 'dataIntelligenceDeepDive',
        focusArea: null,
        myObservation,
      }),
  });

  const inferDefaultEmployeeGroup = inferTypeForFilterStore(
    defaultEmployeeGroup ?? [],
  );
  if (inferDefaultEmployeeGroup.type !== 'string[]') return undefined;

  return {
    dimension: inferDefaultEmployeeGroup.stringArray[0],
    level: inferDefaultEmployeeGroup.stringArray[1],
  };
}

/**
 * Generates a share link for a bias analysis request.
 * Returns undefined if the share link generation fails.
 */
async function generateBiasAnalysisShareLink({
  request,
  userAuthenticatedApiCall,
}: {
  request: BiasAnalysisRequest;
  userAuthenticatedApiCall: Parameters<
    typeof callGenerateShareLink
  >[0]['userAuthenticatedApiCall'];
}): Promise<string | undefined> {
  const shareLinkResult = await callGenerateShareLink({
    windowUrl: window.location.origin,
    customVizRequest: request,
    userAuthenticatedApiCall,
  });
  return shareLinkResult?.shareLink;
}

/**
 * Builds the BiasAnalysisRequest for the deep dive link.
 */
export function buildBiasAnalysisRequest({
  metric,
  dimensionResult,
  metricFocusArea,
  secondaryFiltersPayload,
  lastSuccessfulCustomVizRequest,
  observationLocationType,
}: {
  metric: string;
  dimensionResult: {
    dimension: string;
    treatmentDimensionLevel: string | undefined;
  };
  metricFocusArea: { focusArea: FocusArea };
  secondaryFiltersPayload: Array<{
    columnHeaderValue: string;
    values: string[];
  }>;
  lastSuccessfulCustomVizRequest: { organizationId: string };
  observationLocationType: BiasAnalysisRequest['observationLocationType'];
}): BiasAnalysisRequest {
  return {
    inputs: {
      dropdowns: {},
      extras: {
        metric,
        dimension: dimensionResult.dimension,
        focusArea: metricFocusArea.focusArea,
        filters: secondaryFiltersPayload,
        treatmentDimensionLevel: dimensionResult.treatmentDimensionLevel,
      },
    },
    customVizId: 'biasAnalysis',
    organizationId: lastSuccessfulCustomVizRequest.organizationId,
    observationLocationType,
  };
}

/**
 * Builds the secondary filters payload for the deep dive link.
 */
export function buildSecondaryFiltersPayload({
  filters,
  selectedGroupBy,
  groupByLevel,
  selectedSecondaryBreakdown,
  secondaryBreakdownLevel,
}: {
  filters: Array<{ columnHeaderValue: string; values: string[] }>;
  selectedGroupBy: string | undefined;
  groupByLevel: string;
  selectedSecondaryBreakdown: string | undefined;
  secondaryBreakdownLevel: string;
}): Array<{ columnHeaderValue: string; values: string[] }> {
  return filters.map((filter) => {
    if (filter.columnHeaderValue === selectedGroupBy) {
      return {
        columnHeaderValue: filter.columnHeaderValue,
        values: [groupByLevel],
      };
    }
    if (filter.columnHeaderValue === selectedSecondaryBreakdown) {
      return {
        columnHeaderValue: filter.columnHeaderValue,
        values: [secondaryBreakdownLevel],
      };
    }
    return {
      columnHeaderValue: filter.columnHeaderValue,
      values: filter.values,
    };
  });
}

/**
 * Generates the sample size display string for the tooltip.
 */
export function getSampleSizeDisplay({
  metricDefinition,
  numeratorSize,
  sampleSize,
  language,
}: {
  metricDefinition: MyMetricDefinition | undefined;
  numeratorSize: number;
  sampleSize: number;
  language: ReturnType<typeof useLanguage>;
}): string {
  if (metricDefinition?.isMetricBoolean) {
    return language.subjectsCount({
      subjectsCount: String(numeratorSize),
      totalSample: String(sampleSize),
      whiteLabelingSubjectPlural:
        metricDefinition?.whiteLabeling?.subjectPlural ?? null,
    });
  }
  return language.countPeople({ countPeople: sampleSize });
}

/**
 * Checks if the metric requires grouping to display tooltip content.
 */
export function requiresGroupingForTooltip(
  metricDefinition: MyMetricDefinition | undefined,
  selectedGroupBy: string | null | undefined,
): boolean {
  return !metricDefinition?.isMetricBoolean && !selectedGroupBy;
}

/**
 * Type for compatible data used in table cell building.
 */
export type CompatibleTableData = {
  rows: string[][];
  rowsMeta: Array<{ groupByLevel?: string; secondaryBreakdownLevel?: string }>;
  columnHeaders: string[];
  impactValues: string[][];
  sampleSizes: number[][];
  confidenceValues: string[][];
  fullSampleAverage: string[];
  numeratorSizes?: number[][];
};

/**
 * Calculates the impact value for a cell.
 */
export function getImpactValue(
  columnIndex: number,
  rowIndex: number,
  impactValues: string[][],
): string {
  if (columnIndex > 0 && impactValues.length > 0) {
    return impactValues[rowIndex][columnIndex - 1];
  }
  return '';
}

/**
 * Determines the CSS class name for a cell based on metric type and grouping.
 */
export function getCellClassName(
  isMetricBoolean: boolean | undefined,
  selectedGroupBy: string | null | undefined,
): string {
  const noGroupNoBooleanMetric = !isMetricBoolean && !selectedGroupBy;
  return noGroupNoBooleanMetric
    ? '[&_button]:bg-gray-100 [&_button]:text-gray-600'
    : '[&_button]:bg-gray-100';
}

/**
 * Builds the tooltip item for a data cell.
 */
export function buildTooltipItem({
  columnIndex,
  rowIndex,
  headerVar,
  rowHeader,
  units,
  getColumnDisplayName,
  cell,
  rowMetaForCell,
  getSampleSizeFromColumnName,
  compatibleData,
}: {
  columnIndex: number;
  rowIndex: number;
  headerVar: Array<{ key: string; label: string }>;
  rowHeader: string;
  units: NumericDisplayUnit;
  getColumnDisplayName: (name: string) => string;
  cell: string;
  rowMetaForCell:
    | { groupByLevel?: string; secondaryBreakdownLevel?: string }
    | undefined;
  getSampleSizeFromColumnName: (args: {
    columnIndex: number;
    rowIndex: number;
  }) => number | null;
  compatibleData: CompatibleTableData;
}) {
  const confidence =
    compatibleData.confidenceValues.length > 0
      ? parseFloat(compatibleData.confidenceValues[rowIndex][columnIndex - 1])
      : undefined;

  const fullSampleAverage =
    compatibleData.fullSampleAverage.length > 0
      ? parseFloat(compatibleData.fullSampleAverage[columnIndex - 1])
      : undefined;

  return {
    columnIndex,
    rowIndex,
    header: headerVar[columnIndex - 1],
    rowHeader,
    units,
    title: getColumnDisplayName(headerVar[columnIndex - 1].label),
    value: cell,
    rowMetaForCell,
    sampleSize: getSampleSizeFromColumnName({ columnIndex, rowIndex }) ?? 0,
    confidence,
    fullSampleAverage,
    numeratorSize:
      compatibleData.numeratorSizes?.[rowIndex]?.[columnIndex] ?? 0,
  };
}

/**
 * Builds a single table cell with all its properties.
 */
function buildTableCell({
  cell,
  columnIndex,
  rowIndex,
  rowHeader,
  compatibleData,
  headerVar,
  getMetricFromColumnName,
  formatCellValue,
  selectedGroupBy,
  getColumnDisplayName,
  getSampleSizeFromColumnName,
  bellowColorPalette,
  aboveColorPalette,
  overallDisplayMode,
}: {
  cell: string;
  columnIndex: number;
  rowIndex: number;
  rowHeader: string;
  compatibleData: CompatibleTableData;
  headerVar: Array<{ key: string; label: string }>;
  getMetricFromColumnName: (
    name: string,
  ) => { focusArea: FocusArea; metric: MyMetricDefinition } | null;
  formatCellValue: (args: { rawValue: string; header: string }) => string;
  selectedGroupBy: string | null | undefined;
  getColumnDisplayName: (name: string) => string;
  getSampleSizeFromColumnName: (args: {
    columnIndex: number;
    rowIndex: number;
  }) => number | null;
  bellowColorPalette: ReturnType<typeof generateColorPalette> | undefined;
  aboveColorPalette: ReturnType<typeof generateColorPalette> | undefined;
  overallDisplayMode: OverallDisplayMode;
}) {
  const impactValue = getImpactValue(
    columnIndex,
    rowIndex,
    compatibleData.impactValues,
  );
  const impactValueNumber = parseFloat(impactValue);

  const cellStyle = getBackgroundTextColor({
    impactValue,
    palette: impactValueNumber > 0 ? aboveColorPalette : bellowColorPalette,
    columnIndex,
  });

  let units: NumericDisplayUnit = 'count';
  const headerInfo = headerVar[columnIndex - 1];
  const metricInfo = headerInfo && getMetricFromColumnName(headerInfo.key);

  if (metricInfo) {
    units = determineUnitOfMetric({
      knownMaximum: null,
      isMetricBoolean: metricInfo.metric.isMetricBoolean,
      metricFocusArea: metricInfo.focusArea,
      definitionUnit: metricInfo.metric.unit,
    });
  }

  const displayOverallCount = overallDisplayMode === 'count';

  const rowMetaForCell = compatibleData.rowsMeta[rowIndex];
  const content = computeCellContent({
    columnIndex,
    rowIndex,
    cell,
    compatibleData,
    formatCellValue,
    displayOverallCount,
    metricInfo,
  });

  const className = getCellClassName(
    metricInfo?.metric.isMetricBoolean,
    selectedGroupBy,
  );

  const tooltipItem =
    columnIndex > 0
      ? buildTooltipItem({
          columnIndex,
          rowIndex,
          headerVar,
          rowHeader,
          units,
          getColumnDisplayName,
          cell,
          rowMetaForCell,
          getSampleSizeFromColumnName,
          compatibleData,
        })
      : null;

  return {
    key: `${String(rowIndex)}-${String(columnIndex)}`,
    content,
    className,
    style: cellStyle,
    tooltipItem,
  };
}

/**
 * Tooltip content component for table view cells.
 * Extracted to reduce complexity in the parent component.
 */
function TableViewModeTooltip({
  item,
  metricDefinition,
  selectedGroupBy,
  language,
  getLinkToDeepDive,
}: {
  item: {
    key: string;
    cell: {
      header: { key: string; label: string };
      rowHeader: string;
      title: string;
      value: string;
      units: NumericDisplayUnit;
      sampleSize: number;
      numeratorSize: number;
      confidence: number | undefined;
      fullSampleAverage: number | undefined;
      rowMetaForCell:
        | { groupByLevel?: string; secondaryBreakdownLevel?: string }
        | undefined;
    };
  };
  metricDefinition: MyMetricDefinition | undefined;
  selectedGroupBy: string | null | undefined;
  language: ReturnType<typeof useLanguage>;
  getLinkToDeepDive: (args: {
    metric: string;
    groupByLevel: string;
    secondaryBreakdownLevel: string;
  }) => Promise<string | undefined>;
}): React.ReactNode {
  if (requiresGroupingForTooltip(metricDefinition, selectedGroupBy)) {
    return <MetricRequiresGrouping />;
  }

  const departmentValue = safeParseFloat(item.cell.value);
  if (departmentValue === null) {
    return null;
  }

  const sampleSizeDisplay = getSampleSizeDisplay({
    metricDefinition,
    numeratorSize: item.cell.numeratorSize,
    sampleSize: item.cell.sampleSize,
    language,
  });

  return (
    <TableTooltip
      levelId={item.cell.rowHeader}
      metricDisplayName={item.cell.title}
      metricHeaderValue={item.cell.header.key}
      metricUnits={item.cell.units}
      departmentAverage={{
        value: departmentValue,
        averageDisplayName: language.departmentAverage(),
      }}
      organizationAverage={{
        value: item.cell.fullSampleAverage,
        averageDisplayName: language.organizationAverage(),
      }}
      confidence={item.cell.confidence}
      sampleSizeDisplay={sampleSizeDisplay}
      groupByLevel={item.cell.rowMetaForCell?.groupByLevel || ''}
      secondaryBreakdownLevel={
        item.cell.rowMetaForCell?.secondaryBreakdownLevel || undefined
      }
      getLinkToDeepDive={getLinkToDeepDive}
    />
  );
}

export const TableViewMode = (props: TableViewModeArguments) => {
  const { data: compatibleData, visibleRows, viewConfiguration } = props;

  const {
    baseColors,
    overallDisplayMode = DefaultOverallDisplayMode,
    hideSearch = false,
    displayBorder = true,
  } = viewConfiguration;

  const context = useCustomViz();
  const { lastSuccessfulCustomVizRequest } = context;

  const selectedGroupBy = useMemo(() => {
    if (
      !lastSuccessfulCustomVizRequest ||
      lastSuccessfulCustomVizRequest.customVizId !== 'tableView'
    ) {
      return undefined;
    }
    return getGroupByFromTableViewRequest({
      request: lastSuccessfulCustomVizRequest,
    });
  }, [lastSuccessfulCustomVizRequest]);

  const selectedSecondaryBreakdown = useMemo(() => {
    if (
      !lastSuccessfulCustomVizRequest ||
      lastSuccessfulCustomVizRequest.customVizId !== 'tableView'
    ) {
      return undefined;
    }
    return getSecondaryBreakdownFromTableViewRequest({
      request: lastSuccessfulCustomVizRequest,
    });
  }, [lastSuccessfulCustomVizRequest]);

  const language = useLanguage();
  const { myObservation } = useObservation();

  const getMetricFromColumnName = useCallback(
    (
      columnName: string,
    ): {
      focusArea: FocusArea;
      metric: MyMetricDefinition;
    } | null => {
      if (!myObservation) {
        return null;
      }

      // check if column name is a metric
      for (const fa of myObservation.focusAreas) {
        const metric = fa.metrics.find(
          (m) => m.columnHeaderValue === columnName,
        );

        if (metric) {
          return {
            focusArea: fa.focusArea,
            metric,
          };
        }
      }

      return null;
    },
    [myObservation],
  );

  const getColumnDisplayName = useCallback(
    (columnName: string): string => {
      if (!myObservation) {
        return columnName;
      }

      // check if column name is a dimension
      const dimension = myObservation.dimensions.find(
        (dim) => dim.columnHeaderValue === columnName,
      );

      if (dimension) {
        return language.dimensionDisplayName({
          definition: dimension,
        });
      }

      // check if column name is a metric
      const metricInfo = getMetricFromColumnName(columnName);

      if (metricInfo) {
        return language.metricDisplayName({
          definition: metricInfo.metric,
        });
      }

      // if not found, return the column name as is
      return columnName;
    },
    [language, myObservation, getMetricFromColumnName],
  );

  const getSampleSizeFromColumnName = useCallback(
    ({
      columnIndex,
      rowIndex,
    }: {
      columnIndex: number;
      rowIndex: number;
    }): number | null => {
      if (!compatibleData || compatibleData.sampleSizes.length === 0) {
        return null;
      }

      return compatibleData.sampleSizes[rowIndex][columnIndex];
    },
    [compatibleData],
  );

  const { formatCellValue } = useFormatCellValue();

  const { getAllLevelsForDimension, allDimensions } = useDimensionConfig();

  const headerVar = useMemo(() => {
    if (!compatibleData) return [];
    return compatibleData.columnHeaders.map((header) => ({
      key: header,
      label: getColumnDisplayName(header),
    }));
  }, [compatibleData, getColumnDisplayName]);

  const body = useMemo(() => {
    if (!compatibleData) return [];
    // Generate color palettes from base colors
    const bellowColorPalette = baseColors
      ? generateColorPalette(baseColors[0])
      : undefined;
    const aboveColorPalette = baseColors
      ? generateColorPalette(baseColors[1])
      : undefined;

    const body = compatibleData.rows.map((row, rowIndex) => ({
      key: row[0],
      cells: row.map((cell, columnIndex) =>
        buildTableCell({
          cell,
          columnIndex,
          rowIndex,
          rowHeader: row[0],
          compatibleData,
          headerVar,
          getMetricFromColumnName,
          formatCellValue,
          selectedGroupBy,
          getColumnDisplayName,
          getSampleSizeFromColumnName,
          bellowColorPalette,
          aboveColorPalette,
          overallDisplayMode,
        }),
      ),
    }));

    const filteredBody =
      visibleRows.length > 0
        ? body.filter((_, index) => visibleRows.includes(index))
        : body;

    return filteredBody;
  }, [
    compatibleData,
    baseColors,
    visibleRows,
    headerVar,
    getMetricFromColumnName,
    formatCellValue,
    selectedGroupBy,
    getColumnDisplayName,
    getSampleSizeFromColumnName,
    overallDisplayMode,
  ]);

  const tooltipItemsForGrid = useMemo(
    () =>
      body.flatMap((row) =>
        row.cells
          .map((cell) => {
            if ('tooltipItem' in cell && cell.tooltipItem !== null) {
              return {
                key: cell.key,
                cell: cell.tooltipItem,
              };
            }
            return null;
          })
          .filter((item) => item !== null),
      ),
    [body],
  );

  const { userAuthenticatedApiCall } = useUser();

  const { getFocusAreaByMetric } = useFocusArea();

  const selectedGroupByDimensionDefinition = useMemo(() => {
    if (!selectedGroupBy) {
      return undefined;
    }

    return getDimensionByColumnHeaderValueFromObservation(
      myObservation,
      selectedGroupBy,
    );
  }, [myObservation, selectedGroupBy]);

  const selectedSecondaryBreakdownDimensionDefinition = useMemo(() => {
    if (!selectedSecondaryBreakdown) {
      return undefined;
    }
    return getDimensionByColumnHeaderValueFromObservation(
      myObservation,
      selectedSecondaryBreakdown,
    );
  }, [myObservation, selectedSecondaryBreakdown]);

  const isEmbedMode = useEmbedMode();

  const getLinkToDeepDive = useCallback(
    async ({
      metric,
      groupByLevel,
      secondaryBreakdownLevel,
    }: {
      metric: string;
      // These represent which row was actually clicked, since our table view combines groupBy and secondary breakdown in the same row.
      groupByLevel: string;
      secondaryBreakdownLevel: string;
    }) => {
      // Validate prerequisites and narrow types - combined check for complexity reduction
      if (
        !myObservation ||
        !lastSuccessfulCustomVizRequest ||
        !selectedGroupBy ||
        !canGenerateDeepDiveLink({
          isEmbedMode,
          selectedGroupBy,
          compatibleData,
          myObservation,
          lastSuccessfulCustomVizRequest,
        })
      ) {
        return undefined;
      }

      const rawMetricFocusArea = getFocusAreaByMetric({ metric });
      const validatedFocusArea = getValidatedFocusArea(
        rawMetricFocusArea,
        myObservation,
      );
      const isBiasAnalysisEnabled = getIsBiasAnalysisEnabled(myObservation);
      if (!validatedFocusArea || !isBiasAnalysisEnabled) return undefined;

      // Get dimension levels using helper
      const hasSecondaryBreakdown =
        !!selectedSecondaryBreakdown &&
        !!selectedSecondaryBreakdownDimensionDefinition;

      const groupByDimensionLevel = getDimensionLevel(
        !!selectedGroupByDimensionDefinition,
        myObservation,
        selectedGroupBy,
        groupByLevel,
      );

      const secondaryBreakdownDimensionLevel = getDimensionLevel(
        hasSecondaryBreakdown,
        myObservation,
        selectedSecondaryBreakdown || '',
        secondaryBreakdownLevel,
      );

      // Get default employee group for fallback using helper
      const defaultEmployeeGroupValues = getDefaultEmployeeGroupValues({
        allDimensions,
        getAllLevelsForDimension,
        focusArea: validatedFocusArea.focusArea,
        myObservation,
      });
      if (!defaultEmployeeGroupValues) return undefined;

      // Determine dimension and treatment level
      const dimensionResult = determineDimensionAndTreatmentLevel({
        groupByDimensionLevel,
        selectedGroupBy,
        groupByLevel,
        selectedSecondaryBreakdownDimensionDefinition,
        secondaryBreakdownDimensionLevel,
        secondaryBreakdownLevel,
        defaultDimension: defaultEmployeeGroupValues.dimension,
        defaultTreatmentDimensionLevel: defaultEmployeeGroupValues.level,
      });

      if (!dimensionResult) return undefined;

      // Type assertion needed for complex union type from lastSuccessfulCustomVizRequest
      const requestFilters = (
        lastSuccessfulCustomVizRequest.inputs.extras as {
          filters: Array<{ columnHeaderValue: string; values: string[] }>;
        }
      ).filters;

      // Build filters and request, then generate share link
      const secondaryFiltersPayload = buildSecondaryFiltersPayload({
        filters: requestFilters,
        selectedGroupBy,
        groupByLevel,
        selectedSecondaryBreakdown,
        secondaryBreakdownLevel,
      });

      const request = buildBiasAnalysisRequest({
        metric,
        dimensionResult,
        metricFocusArea: validatedFocusArea,
        secondaryFiltersPayload,
        lastSuccessfulCustomVizRequest,
        observationLocationType: myObservation.type,
      });

      return generateBiasAnalysisShareLink({
        request,
        userAuthenticatedApiCall,
      });
    },
    [
      isEmbedMode,
      allDimensions,
      compatibleData,
      getAllLevelsForDimension,
      getFocusAreaByMetric,
      lastSuccessfulCustomVizRequest,
      myObservation,
      selectedGroupBy,
      selectedGroupByDimensionDefinition,
      selectedSecondaryBreakdown,
      selectedSecondaryBreakdownDimensionDefinition,
      userAuthenticatedApiCall,
    ],
  );

  const tooltipProps = useTouchableTooltipCollection({
    items: tooltipItemsForGrid,
    tooltipFn: (item) => (
      <TableViewModeTooltip
        item={item}
        metricDefinition={getMetricFromColumnName(item.cell.header.key)?.metric}
        selectedGroupBy={selectedGroupBy}
        language={language}
        getLinkToDeepDive={getLinkToDeepDive}
      />
    ),
  });

  return (
    <TableGrid
      backgroundColorClass={'bg-white'}
      tableClassName={'rounded-md '}
      hideSearch={hideSearch}
      shouldScroll={true}
      header={headerVar}
      body={body}
      tooltipProps={tooltipProps}
      displayBorder={displayBorder}
    />
  );
};
